import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Search, Trash2 } from 'lucide-react';
import {
  api,
  type AuthUser,
  type Player,
  type WorstParkerPickDto,
} from '@/lib/api';

function mondayOf(dateIso: string): string {
  const d = new Date(dateIso + 'T00:00:00Z');
  const day = d.getUTCDay();
  const offset = day === 0 ? 6 : day - 1;
  const m = new Date(d.getTime() - offset * 24 * 60 * 60 * 1000);
  return m.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function playerLabel(p: { name: string; displayName: string | null }): string {
  return p.displayName?.trim() || p.name;
}

function shiftWeeks(monday: string, delta: number): string {
  const d = new Date(monday + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + delta * 7);
  return d.toISOString().slice(0, 10);
}

export default function AdminWorstParker({ user }: { user: AuthUser }) {
  const canEdit = user.role === 'R5' || user.role === 'R4';

  const [week, setWeek] = useState<string>(() => mondayOf(todayIso()));

  const q = useQuery({
    queryKey: ['admin-worst-parker', week],
    queryFn: () => api.getAdminWorstParker(week),
    enabled: canEdit,
  });
  const recent = useQuery({
    queryKey: ['admin-worst-parker', 'recent'],
    queryFn: () => api.getAdminWorstParker(),
    enabled: canEdit,
  });
  const playersQ = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
    enabled: canEdit,
  });

  const pick = q.data?.pick ?? null;

  if (!canEdit) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-3 p-4">
        <h1 className="text-xl font-semibold">Worst parker</h1>
        <p className="rounded border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-400">
          R4/R5 required.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4">
      <header>
        <h1 className="text-xl font-semibold">Worst parker of the week</h1>
        <p className="text-sm text-slate-400">
          One player per week, shown as a tile in the alliance hub with a
          random line from the <code>worst_parker</code> pool.
        </p>
      </header>

      <WeekPicker week={week} onChange={setWeek} />

      <PickEditor
        key={week}
        week={week}
        current={pick}
        loading={q.isLoading}
        players={playersQ.data ?? []}
      />

      <RecentPicks recent={recent.data?.recent ?? []} onPick={setWeek} />
    </div>
  );
}

function WeekPicker({
  week,
  onChange,
}: {
  week: string;
  onChange: (w: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded border border-slate-800 bg-slate-900/40 px-3 py-2">
      <button
        type="button"
        onClick={() => onChange(shiftWeeks(week, -1))}
        className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
      >
        ← prev
      </button>
      <input
        type="date"
        value={week}
        onChange={(e) => onChange(mondayOf(e.target.value))}
        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
      />
      <button
        type="button"
        onClick={() => onChange(shiftWeeks(week, 1))}
        className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
      >
        next →
      </button>
      <button
        type="button"
        onClick={() => onChange(mondayOf(todayIso()))}
        className="ml-auto rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
      >
        this week
      </button>
    </div>
  );
}

function PickEditor({
  week,
  current,
  loading,
  players,
}: {
  week: string;
  current: WorstParkerPickDto | null;
  loading: boolean;
  players: Player[];
}) {
  const qc = useQueryClient();
  const [playerId, setPlayerId] = useState<number | null>(
    current?.player.id ?? null,
  );
  const [note, setNote] = useState<string>(current?.note ?? '');

  const activePlayers = useMemo(
    () => players.filter((p) => p.active).sort((a, b) =>
      playerLabel(a).localeCompare(playerLabel(b), undefined, {
        sensitivity: 'base',
      }),
    ),
    [players],
  );

  const saveMut = useMutation({
    mutationFn: (body: {
      weekStart: string;
      playerId: number;
      note?: string | null;
    }) => api.putAdminWorstParker(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-worst-parker'] });
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.deleteAdminWorstParker(week),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-worst-parker'] });
      setPlayerId(null);
      setNote('');
    },
  });

  return (
    <section className="space-y-3 rounded border border-slate-800 bg-slate-900/40 p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Week of {week}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex flex-col gap-1 text-xs text-slate-400">
              Player
              <PlayerCombobox
                players={activePlayers}
                selectedId={playerId}
                onSelect={setPlayerId}
              />
            </div>
            <label className="flex flex-col gap-1 text-xs text-slate-400">
              Note (optional)
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={200}
                placeholder="e.g. dropped next to two max R5s"
                className="rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={playerId == null || saveMut.isPending}
              onClick={() =>
                saveMut.mutate({
                  weekStart: week,
                  playerId: playerId!,
                  note: note.trim() ? note.trim() : null,
                })
              }
              className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
            >
              {saveMut.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              Save
            </button>
            {current && (
              <button
                type="button"
                disabled={deleteMut.isPending}
                onClick={() => {
                  if (confirm('Remove the worst-parker pick for this week?')) {
                    deleteMut.mutate();
                  }
                }}
                className="inline-flex items-center gap-1 rounded border border-rose-700 bg-rose-950/40 px-3 py-1.5 text-sm text-rose-100 hover:bg-rose-900/40 disabled:opacity-50"
              >
                <Trash2 size={14} /> Clear
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function PlayerCombobox({
  players,
  selectedId,
  onSelect,
}: {
  players: Player[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selected = useMemo(
    () => players.find((p) => p.id === selectedId) ?? null,
    [players, selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => {
      const label = (p.displayName?.trim() || p.name).toLowerCase();
      return label.includes(q) || p.name.toLowerCase().includes(q);
    });
  }, [players, query]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    setQuery('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (id: number) => {
    onSelect(id);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded border px-2 py-1.5 text-left text-sm ${
          selected
            ? 'border-slate-700 bg-slate-950 text-slate-100 hover:border-slate-600'
            : 'border-dashed border-slate-700 bg-slate-950/60 text-slate-500 hover:border-slate-600'
        }`}
      >
        {selected ? (
          <span className="truncate">
            <span className="font-medium">{playerLabel(selected)}</span>{' '}
            <span className="text-xs text-slate-500">({selected.rank})</span>
          </span>
        ) : (
          <span>Pick a player…</span>
        )}
        <Search size={14} className="shrink-0 text-slate-500" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(filtered.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const p = filtered[highlight];
                if (p) pick(p.id);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
            placeholder="Search player…"
            className="w-full border-b border-slate-800 bg-slate-950 px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No match.</li>
            )}
            {filtered.map((p, i) => {
              const label = p.displayName?.trim() || p.name;
              const isSel = p.id === selectedId;
              const isHi = i === highlight;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(p.id)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm ${
                      isHi ? 'bg-slate-800' : ''
                    } ${isSel ? 'text-indigo-300' : ''}`}
                  >
                    <span className="truncate">
                      {label}
                      {p.displayName && p.displayName !== p.name && (
                        <span className="ml-2 text-xs text-slate-500">
                          {p.name}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs text-slate-500">
                      {p.rank}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function RecentPicks({
  recent,
  onPick,
}: {
  recent: WorstParkerPickDto[];
  onPick: (w: string) => void;
}) {
  if (recent.length === 0) return null;
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
        Recent picks
      </h2>
      <ul className="divide-y divide-slate-800 rounded border border-slate-800">
        {recent.map((r) => (
          <li key={r.weekStart} className="flex items-center gap-3 px-3 py-2">
            <button
              type="button"
              onClick={() => onPick(r.weekStart)}
              className="text-xs tabular-nums text-indigo-300 hover:underline"
            >
              {r.weekStart}
            </button>
            <span className="text-sm text-slate-100">
              {playerLabel(r.player)}
            </span>
            {r.note && (
              <span className="truncate text-xs italic text-slate-400">
                — {r.note}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
